/**
 * What the transports read from the active-firm store: the `x-cova-org` claim
 * for the scope a request is made under (spec multi-product 005 §4 "Platform
 * web", 007 §2.1; board unit WP1.24.8).
 *
 * Read per request, never captured, so a switch applies to the very next
 * request. No firm for the scope ⇒ no header, and the server resolves the
 * person's one membership.
 *
 * The scope is NAMED by the caller, never read back off a header: the client
 * sends no scope header (022 G48b, WP3.17.2a-b2). The server takes the scope
 * from the acting firm, so the only thing a client's scope still selects is
 * WHICH firm this tab claims.
 */
import type { StoreApi } from "zustand"

import { ORG_HEADER } from "@/domain/organisation/requested-org"
import type { PlatformScope } from "@/domain/scope/platform-scope"

import { activeFirmStore, type ActiveFirmStore } from "./use-active-firm-store"

type HeadersFn = () => Readonly<Record<string, string>>

/** The firm this tab claims for `scope`, or null. */
export const activeFirmFor = (
  scope: PlatformScope,
  store: StoreApi<ActiveFirmStore> = activeFirmStore,
): string | null => store.getState().firms[scope] ?? null

/**
 * A header function (the `AgentHost.rpc.headers` contract) that claims this
 * tab's firm for `scope` on every request, or sends nothing when the tab has
 * none. Returns a new function: the caller memoises it (or holds it as a module
 * constant), because the agent package registers the function by identity.
 */
export const activeFirmHeaders =
  (scope: PlatformScope, store: StoreApi<ActiveFirmStore> = activeFirmStore): HeadersFn =>
  (): Readonly<Record<string, string>> => {
    const orgId = activeFirmFor(scope, store)
    if (orgId === null) return {}
    return { [ORG_HEADER]: orgId }
  }
